import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { MemberAuthProvider } from "@/contexts/MemberAuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdminLayout from "@/components/AdminLayout";
import Home from "@/pages/Home";
import Music from "@/pages/Music";
import Videos from "@/pages/Videos";
import Store from "@/pages/Store";
import Affiliate from "@/pages/Affiliate";
import Label from "@/pages/Label";
import Contact from "@/pages/Contact";
import Gallery from "@/pages/Gallery";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/admin/Dashboard";
import MusicManager from "@/pages/admin/MusicManager";
import VideoManager from "@/pages/admin/VideoManager";
import ImageManager from "@/pages/admin/ImageManager";
import HomepageManager from "@/pages/admin/HomepageManager";
import StoreManager from "@/pages/admin/StoreManager";
import AffiliateManager from "@/pages/admin/AffiliateManager";
import AlbumsManager from "@/pages/admin/AlbumsManager";
import MyDownloads from "@/pages/MyDownloads";
import OrdersManager from "@/pages/admin/OrdersManager";
import Settings from "@/pages/admin/Settings";
import AnalyticsCommandCenter from "@/pages/admin/AnalyticsCommandCenter";
import TiersManager from "@/pages/admin/TiersManager";
import MembersCRM from "@/pages/admin/MembersCRM";
import EmailCampaigns from "@/pages/admin/EmailCampaigns";
import RewardUnlocksManager from "@/pages/admin/RewardUnlocksManager";
import MissionsManager from "@/pages/admin/MissionsManager";
import VaultManager from "@/pages/admin/VaultManager";
import GrowthDashboard from "@/pages/admin/GrowthDashboard";
import Membership from "@/pages/Membership";
import Join from "@/pages/Join";
import MemberLogin from "@/pages/MemberLogin";
import Account from "@/pages/Account";
import Vault from "@/pages/Vault";
import Leaderboards from "@/pages/Leaderboards";
import TrackingTags from "@/components/TrackingTags";
import { initAnalytics, trackPageView } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsRouter() {
  const { pathname } = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

function PublicLayout() {
  return (
    <>
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/music" element={<Music />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/store" element={<Store />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/my-downloads" element={<MyDownloads />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/label" element={<Label />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/join" element={<Join />} />
          <Route path="/login" element={<MemberLogin />} />
          <Route path="/account" element={<Account />} />
          <Route path="/vault" element={<Vault />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect OAuth callback session_id synchronously before any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      <AnalyticsRouter />
      <TrackingTags />
      {isAdmin ? (
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="analytics" element={<AnalyticsCommandCenter />} />
            <Route path="music" element={<MusicManager />} />
            <Route path="albums" element={<AlbumsManager />} />
            <Route path="videos" element={<VideoManager />} />
            <Route path="images" element={<ImageManager />} />
            <Route path="homepage" element={<HomepageManager />} />
            <Route path="store" element={<StoreManager />} />
            <Route path="affiliate" element={<AffiliateManager />} />
            <Route path="orders" element={<OrdersManager />} />
            <Route path="tiers" element={<TiersManager />} />
            <Route path="members" element={<MembersCRM />} />
            <Route path="campaigns" element={<EmailCampaigns />} />
            <Route path="rewards" element={<RewardUnlocksManager />} />
            <Route path="missions" element={<MissionsManager />} />
            <Route path="vault" element={<VaultManager />} />
            <Route path="growth" element={<GrowthDashboard />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      ) : (
        <PublicLayout />
      )}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MemberAuthProvider>
          <div className="min-h-screen bg-[#050505] text-white font-body">
            <AppRoutes />
            <Toaster
              theme="dark"
              toastOptions={{
                style: {
                  background: "#111111",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                },
              }}
            />
          </div>
        </MemberAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
